/**
 * ورود و ثبت‌نام — قرینه‌ی apps/mobile/app/auth.tsx.
 *
 * همون منطق و همون پیام‌های آدمیزاد؛ دو تا فرقِ واقعی با موبایل:
 *  ۱. ورود با گوگل در مرورگر «ری‌دایرکت» است: کاربر از صفحه می‌ره و با نشست
 *     برمی‌گرده. اپِ موبایل به‌جاش با deep link (goout://) برمی‌گرده.
 *  ۲. لینکِ تاییدِ ایمیل باید به همین دامنه برگرده، نه به goout://.
 *
 * ساختِ پروفایل (قدمِ دوم بعد از ثبت‌نام) هنوز اینجا نیست — بخشی از برشِ
 * «پروفایل» در فاز ۴ است. تا اون‌موقع کاربرِ بی‌پروفایل پیامش رو می‌بینه.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { isSupabaseConfigured, supabase } from '@/core/lib/supabase';
import { passwordProblem } from '@/core/lib/password';
import { t, tAround, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { visibleOAuthButtons, type OAuthProvider } from '@/core/lib/authProviders';
import { noteOAuthProvider } from '@/core/lib/appleRevoke';
import { rememberAfterAuth, takeAfterAuth } from '@/core/lib/afterAuth';
import { Btn, Card, Chip, Field, Note } from '@/ui/kit';
import { useTheme } from '@/ui/theme';

type Mode = 'signin' | 'signup';

/** پیام‌های خام Supabase انگلیسی و فنی‌ان؛ این‌ها رو به چیزی قابل‌فهم ترجمه می‌کنیم. */
function humanError(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes('invalid login credentials')) return t('authWrongCredentials');
  if (m.includes('already registered') || m.includes('already exists')) return t('authEmailTaken');
  if (m.includes('email not confirmed')) return t('authNotConfirmed');
  if (m.includes('password should be')) return t('authWeakPassword');
  if (m.includes('unable to validate email') || m.includes('invalid email')) return t('authBadEmail');
  if (m.includes('network') || m.includes('failed to fetch')) return t('authNetwork');
  if (m.includes('provider is not enabled') || m.includes('unsupported provider')) {
    return t('authProviderOff');
  }
  return raw;
}

/**
 * لینکِ برگشت برای وب. عمداً از `window.location.origin` خونده می‌شه و
 * hardcode نیست، چون آدرسِ لوکال، پیش‌نمایش و دامنه‌ی نهایی سه‌تا چیزِ متفاوتن.
 * هر سه باید در «Redirect URLs»ِ داشبوردِ Supabase ثبت بشن، وگرنه Supabase
 * لینک را به Site URL برمی‌گردونه و کاربر جای دیگه‌ای می‌افته.
 */
const webRedirect = () => `${window.location.origin}/`;

/** «با ادامه دادن، Terms of Use (EULA) را می‌پذیرید» — لینک وسطِ جمله، جایش به زبان بستگی دارد. */
function TermsNote() {
  const [before, after] = tAround('authTermsNote', 'terms');
  return (
    <p className="dim auth-terms" data-testid="auth-terms">
      {before}<Link to="/legal">{t('authTermsLink')}</Link>{after}
    </p>
  );
}

/**
 * `dest`: جایی که بعد از ورود باید برگشت (`AuthRoute` از `state`/`?from=`
 * می‌خواندش). فقط ورودِ گوگل/اپل لازمش دارد — نگاه کن به `oauth`.
 */
export function LoginPage({ dest = null }: { dest?: string | null } = {}) {
  useLocale();
  const { isDark } = useTheme();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  /** تکرارِ رمز — فقط سرِ ثبت‌نام. یک تایپِ اشتباه در فیلدِ نقطه‌نقطه یعنی
      حسابی که از روزِ اول رمزش را نمی‌دانی. */
  const [password2, setPassword2] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** ایمیلی که ثبت‌نام شده ولی هنوز تایید نشده. تا وقتی پُره، «ایمیلت رو چک کن». */
  const [awaiting, setAwaiting] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null); setMsg(null);
    try {
      await fn();
    } catch (e) {
      reportError('web.auth', e);
      setError(humanError(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  };

  // بعد از ورودِ موفق هیچ ناوبریِ دستی لازم نیست: SessionProvider نشست رو
  // می‌بینه و روتر خودش صفحه رو عوض می‌کنه (نگاه کن به app/routes.tsx).
  const signIn = () => run(async () => {
    const { error: err } = await supabase.auth.signInWithPassword({
      email: email.trim(), password,
    });
    if (err) {
      if (err.message.toLowerCase().includes('email not confirmed')) {
        setAwaiting(email.trim());
        return;
      }
      throw err;
    }
  });

  const signUp = () => run(async () => {
    // کوتاهی و سادگی همان لحظه‌ی تایپ زیرِ فیلد گفته می‌شوند؛ این نگهبانِ
    // دوم است تا با Enter یا کلیکِ سریع هم رمزِ بد به Supabase نرسد.
    const pw = passwordProblem(password);
    if (pw) { setError(t(pw === 'short' ? 'authPasswordShort' : 'authPasswordSimple')); return; }
    if (password !== password2) { setError(t('authPasswordMismatch')); return; }
    const { data, error: err } = await supabase.auth.signUp({
      email: email.trim(), password, options: { emailRedirectTo: webRedirect() },
    });
    if (err) throw err;
    if (!data.session) setAwaiting(email.trim());
  });

  /**
   * ورود با ارائه‌دهنده‌ی بیرونی. بعد از این خط، مرورگر از صفحه می‌ره — پس نه پیامِ موفقیتی
   * اینجا معنی داره و نه چیزی برای ناوبری. برگشت با نشست، به `/` می‌رسه و
   * چون کلاینت با `detectSessionInUrl` ساخته شده، خودش توکن را از آدرس برمی‌داره.
   */
  const oauth = (provider: OAuthProvider) => run(async () => {
    // تا وقتِ برگشت معلوم باشد توکنِ همراهِ نشست مالِ کیست (WHOSUP-251).
    await noteOAuthProvider(provider);
    /*
     * مقصد همین لحظه دوباره نوشته می‌شود، نه فقط وقتِ باز شدنِ فرم (WHOSUP-359).
     * کلیدش برای همه‌ی تب‌ها یکی است، و یک تبِ دیگر که روی صفحه‌ی ورود مانده
     * بود (مثلاً بعد از خروج روی یک برنامه) مقصدِ خودش را رویش نوشته بود — نتیجه:
     * ورود از صفحه‌ی رویداد سر از برنامه‌ی کسِ دیگری با «Error» درمی‌آورد.
     * بی‌مقصد یعنی برگشت به `/`، پس باقی‌مانده‌ی تبِ دیگر هم پاک می‌شود.
     */
    if (dest) await rememberAfterAuth(dest);
    else await takeAfterAuth();
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider, options: { redirectTo: webRedirect() },
    });
    if (err) throw err;
  });

  const resend = () => run(async () => {
    const { error: err } = await supabase.auth.resend({
      type: 'signup', email: awaiting ?? email.trim(),
      options: { emailRedirectTo: webRedirect() },
    });
    if (err) throw err;
    setMsg(t('authVerifySent'));
  });

  const forgot = () => run(async () => {
    if (!email.trim()) { setError(t('authNeedEmailFirst')); return; }
    // مقصد **صفحه‌ی گذاشتنِ رمز** است، نه صفحه‌ی اول. قبلاً به `/` می‌رفت و
    // کاربر فقط وارد می‌شد بدونِ اینکه هیچ‌وقت رمزی بگذارد.
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (err) throw err;
    setMsg(t('authResetSent'));
  });

  return (
    <div className="auth-page">
      <div className="auth-hero">
        <h1>
          <img className="auth-brand-logo"
            src={isDark ? '/logo-horizontal-dark.svg' : '/logo-horizontal.svg'}
            alt="PalsAndPlans" width="1280" height="256" />
        </h1>
        <p>{t('tagline')}</p>
      </div>

      {!isSupabaseConfigured() ? <Note tone="warn">{t('backendNotConfigured')}</Note> : null}

      {awaiting ? (
        <Card>
          <h3>{t('authVerifyTitle')}</h3>
          <p className="dim">{awaiting}</p>
          <Note tone="brand">{t('authVerifyBody')}</Note>
          {msg ? <p className="muted">{msg}</p> : null}
          {error ? <p className="error">{error}</p> : null}
          <Btn kind="ghost" block label={busy ? '…' : t('authResend')}
            disabled={busy} onClick={resend} />
          <Btn kind="ghost" block label={t('authBackToSignIn')}
            disabled={busy} onClick={() => { setAwaiting(null); setMode('signin'); }} />
        </Card>
      ) : (
        <Card>
          <div className="chiprow">
            <Chip label={t('authSignIn')} selected={mode === 'signin'}
              onClick={() => setMode('signin')} />
            <Chip label={t('authSignUp')} selected={mode === 'signup'}
              onClick={() => setMode('signup')} />
          </div>

          <Field label={t('email')} type="email" autoComplete="email"
            value={email} onChange={setEmail} />
          <Field label={t('password')} type="password"
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            value={password} onChange={(v) => { setPassword(v); setError(null); }} />

          {/* رمز دوبار، فقط سرِ ثبت‌نام — و کوتاهی/سادگی/ناهم‌خوانی همان‌جا
              زیرِ فیلد گفته می‌شوند، نه بعد از زدنِ دکمه. قاعده‌ی «ساده
              نباشد» در core/lib/password است، مشترک با اپ و «رمزِ تازه». */}
          {mode === 'signup' ? (
            <>
              {password !== '' && passwordProblem(password) ? (
                <p className="field-note bad">
                  {t(passwordProblem(password) === 'short' ? 'authPasswordShort' : 'authPasswordSimple')}
                </p>
              ) : null}
              <Field label={t('authNewPasswordAgain')} type="password"
                autoComplete="new-password" value={password2} onChange={(v) => { setPassword2(v); setError(null); }} />
              {password2 && password2 !== password ? (
                <p className="field-note bad">{t('authPasswordMismatch')}</p>
              ) : null}
            </>
          ) : null}

          {/* این‌جا تیکِ پذیرش نیست: تیک روی قدمِ ساختِ نمایه است
              (CreateProfilePage) چون تنها قدمی است که ورود با گوگل هم از آن
              رد می‌شود، و شرطِ ساختِ حساب همان می‌ماند.

              ولی اپل نوشته شرایط باید «پیش از ثبت‌نام یا ورود» دیده شود، و
              تیک بعد از برگه‌ی Apple/گوگل می‌آید — کاربرِ قدیمی هم اصلاً
              نمی‌بیندش. پس این سطر بالای همه‌ی دکمه‌های ورود است، با لینک به
              متنِ کامل. قرینه‌ی `TermsNote` در auth.tsxِ موبایل. */}

          {error ? <p className="error">{error}</p> : null}
          {msg ? <p className="muted">{msg}</p> : null}

          <TermsNote />

          <Btn
            block
            testId="auth-submit"
            label={busy ? '…' : mode === 'signin' ? t('authSignIn') : t('authSignUp')}
            disabled={busy || !email.trim() || !password
              || (mode === 'signup' && (!password2 || password2 !== password
                || passwordProblem(password) !== null))}
            onClick={mode === 'signin' ? signIn : signUp}
          />

          {mode === 'signin' ? (
            <Btn kind="ghost" block label={t('authForgot')} disabled={busy} onClick={forgot} />
          ) : null}

          <hr className="hr" />
          <p className="dim" style={{ textAlign: 'center' }}>{t('orContinueWith')}</p>
          {/* ورودِ بیرونی برای کسی که حساب نداره همون‌جا حساب می‌سازه — یعنی
              این هم ثبت‌نامه. پس شرطِ پذیرش این‌جا نیست بلکه یک قدم جلوتر،
              روی ساختِ نمایه، جایی که هر دو راه به آن می‌رسند.

              فهرست و ترتیبش از `core/lib/authProviders` می‌آید، همان جایی که
              موبایل هم از آن می‌خواند: Apple باید دستِ‌کم به‌اندازه‌ی بقیه
              دیده شود (بندِ ۴.۸ اپل) و آن قاعده نباید در دو فایل نگه‌داری شود. */}
          {visibleOAuthButtons().map((b) => (
            <Btn key={b.provider} kind="outline" block label={t(b.labelKey)}
              disabled={busy} onClick={() => oauth(b.provider)} />
          ))}
        </Card>
      )}
    </div>
  );
}
